import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ExternalLink, FileText, Clock, User } from "lucide-react";
import {
  getApplication,
  withdrawApplication,
  changeApplicationStatus,
  resolveFileUrl,
} from "@/lib/jobsApi";
import ErrorState from "@/pages/jobs/ErrorState";

/**
 * Iter-263 §6/§7 shared application detail.
 *
 * `GET /applications/{id}` works for BOTH applicant and employer roles and
 * returns `role: "applicant" | "employer"` alongside the full snapshot.
 * ⚠️ When the employer opens a `submitted` application the backend
 * auto-flips it to `viewed` (and notifies the applicant) — so a simple
 * GET on mount is the intended UX.
 */
const NEXT_STATUSES = ["viewed", "shortlisted", "interviewing", "rejected", "hired"];

const STATUS_STYLE = {
  submitted: "bg-slate-100 text-slate-700",
  viewed: "bg-blue-50 text-blue-700",
  shortlisted: "bg-amber-50 text-amber-700",
  interviewing: "bg-indigo-50 text-indigo-700",
  hired: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  withdrawn: "bg-slate-50 text-slate-500",
};

export default function JobApplicationDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["application", id], queryFn: () => getApplication(id) });

  const withdraw = useMutation({
    mutationFn: () => withdrawApplication(id),
    onSuccess: () => {
      toast.success("Application withdrawn.");
      qc.invalidateQueries({ queryKey: ["application", id] });
      qc.invalidateQueries({ queryKey: ["my-applications"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Cannot withdraw"),
  });

  const statusMut = useMutation({
    mutationFn: (status) => changeApplicationStatus({ ids: [id], status }),
    onSuccess: (_data, status) => {
      toast.success(`Status set to ${status}.`);
      qc.invalidateQueries({ queryKey: ["application", id] });
      qc.invalidateQueries({ queryKey: ["listing-applications"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Status update failed"),
  });

  if (q.isLoading) return <Shell><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></Shell>;
  if (q.isError) return <Shell><ErrorState onRetry={() => q.refetch()} /></Shell>;

  const app = q.data?.application || q.data || {};
  const role = q.data?.role || app.role;
  // Real backend returns `application.applicant` — keep `snapshot` as a
  // defensive fallback so the page also works if the API layer ever
  // normalises to snapshot in the future.
  const snap = app.applicant || app.snapshot || {};
  const answers = app.answers || [];
  const history = app.status_history || [];
  const isEmployer = role === "employer";
  const canWithdraw =
    !isEmployer && !["withdrawn", "rejected", "hired"].includes(app.status);

  return (
    <Shell>
      <button
        onClick={() => nav(-1)}
        className="text-sm text-slate-500 hover:text-[color:var(--brand-blue)] inline-flex items-center gap-1"
        data-testid="application-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="mt-3 flex items-start gap-3 flex-wrap">
        <h1 className="flex-1 font-display text-2xl md:text-3xl font-black text-slate-900" data-testid="application-title">
          {app.job_title || "Application"}
        </h1>
        <span
          data-testid="application-status"
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLE[app.status] || "bg-slate-100 text-slate-500"}`}
        >
          {app.status}
        </span>
      </div>
      <div className="text-xs text-slate-500 mt-1">
        {app.employer_name && <>Employer: {app.employer_name} · </>}
        Applied {app.submitted_at ? new Date(app.submitted_at).toLocaleString("en-NZ") : "—"}
      </div>

      {/* Employer status actions */}
      {isEmployer && !["withdrawn", "hired", "rejected"].includes(app.status) && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="application-employer-actions">
          <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Set status:</span>
          {NEXT_STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`set-status-${s}`}
              disabled={statusMut.isPending || app.status === s}
              onClick={() => statusMut.mutate(s)}
              className="text-xs font-semibold rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-3 py-1.5 disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {canWithdraw && (
        <button
          data-testid="application-withdraw"
          onClick={() => withdraw.mutate()}
          disabled={withdraw.isPending}
          className="mt-4 text-xs font-semibold text-red-500 hover:underline disabled:opacity-50"
        >
          Withdraw application
        </button>
      )}

      {/* Applicant snapshot (employer sees this, applicant sees what employer sees) */}
      <section className="mt-6 rounded-2xl bg-white border border-soft p-5 md:p-6" data-testid="application-snapshot">
        <div className="flex items-center gap-3">
          {snap.photo_url ? (
            <img
              src={snap.photo_url}
              alt=""
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-14 h-14 rounded-full object-cover ring-2 ring-slate-200"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-slate-100 grid place-items-center text-slate-400">
              <User className="w-6 h-6" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold text-slate-900">{snap.full_name || "Applicant"}</div>
            <div className="text-xs text-slate-500 truncate">{snap.email} · {snap.phone}</div>
            {snap.headline && <div className="text-xs text-slate-600 italic mt-0.5">{snap.headline}</div>}
            {snap.location && <div className="text-xs text-slate-500">{snap.location}</div>}
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-600">
          Right to work: <span className="font-semibold text-slate-800">{app.right_to_work_status || snap.right_to_work_status || "—"}</span>
        </div>
      </section>

      {/* Documents */}
      <section className="mt-4 rounded-2xl bg-white border border-soft p-5 md:p-6" data-testid="application-documents">
        <h2 className="font-display font-bold text-lg">Documents</h2>
        <ul className="mt-2 space-y-2">
          {app.resume && (
            <DocRow label="Resume" file={app.resume} testId="application-resume" />
          )}
          {app.cover_letter && (
            <DocRow label="Cover letter" file={app.cover_letter} testId="application-cover" />
          )}
          {app.portfolio_url && (
            <li className="flex items-start gap-3 rounded-xl border border-subtle px-3 py-2" data-testid="application-portfolio">
              <ExternalLink className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Portfolio / work sample</div>
                {/* Iter-265: untrusted user-supplied URL — render the raw
                    href (no masking) and open with rel noopener/noreferrer. */}
                <a
                  href={app.portfolio_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm break-all text-[color:var(--brand-blue)] hover:underline"
                  data-testid="application-portfolio-link"
                >
                  {app.portfolio_url}
                </a>
              </div>
            </li>
          )}
          {!app.resume && !app.cover_letter && !app.portfolio_url && (
            <li className="text-xs text-slate-500">No documents attached.</li>
          )}
        </ul>
      </section>

      {/* Answers */}
      {answers.length > 0 && (
        <section className="mt-4 rounded-2xl bg-white border border-soft p-5 md:p-6" data-testid="application-answers">
          <h2 className="font-display font-bold text-lg">Screening answers</h2>
          <ul className="mt-3 space-y-3">
            {answers.map((a, i) => (
              <li key={a.question_id || i} className="rounded-xl border border-subtle p-3">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{a.question_text || `Question ${i + 1}`}</div>
                <div className="mt-1 text-sm text-slate-800">
                  {Array.isArray(a.answer) ? a.answer.join(", ") : String(a.answer ?? "—")}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Status history */}
      {history.length > 0 && (
        <section className="mt-4 rounded-2xl bg-white border border-soft p-5 md:p-6" data-testid="application-history">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Clock className="w-4 h-4" /> Status history</h2>
          <ol className="mt-3 space-y-2 text-sm">
            {history.map((h, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLE[h.status] || "bg-slate-100 text-slate-500"}`}>{h.status}</span>
                <span className="text-xs text-slate-500">{h.at ? new Date(h.at).toLocaleString("en-NZ") : ""}</span>
                {h.by_name && <span className="text-xs text-slate-500">· by {h.by_name}</span>}
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="mt-6 text-sm">
        <Link to={`/ad/${app.job_listing_id}`} className="text-[color:var(--brand-blue)] hover:underline">
          View job listing →
        </Link>
        {isEmployer && (
          <Link
            to={`/jobs/employer?listing=${app.job_listing_id}`}
            className="ml-4 text-[color:var(--brand-blue)] hover:underline"
            data-testid="application-back-to-list"
          >
            Back to applicants
          </Link>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">{children}</div>;
}

function DocRow({ label, file, testId }) {
  return (
    <li className="flex items-center gap-3 rounded-xl border border-subtle px-3 py-2" data-testid={testId}>
      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
        <div className="text-sm text-slate-800 truncate">{file.file_name || "Document"}</div>
      </div>
      {file.file_url && (
        <a
          href={resolveFileUrl(file.file_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[color:var(--brand-blue)] hover:underline text-xs font-semibold inline-flex items-center gap-1"
          data-testid={`${testId}-open`}
        >
          Open <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </li>
  );
}
